import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  FilterPanelContext,
  nextOpenControl,
} from '@/features/common/FilterPanel/filterPanelControls';
import type { FilterPanelProps } from '@/features/common/FilterPanel/types/FilterPanelProps';
// The fields' look too: they only ever render inside the panel. The row's rules for the controls
// carry the controls' own size class, so they outrank dropdown.css whichever sheet loads first.
import '@/features/common/FilterPanel/filter-panel.css';

/**
 * A public page's filter card: its fields on one row, each label over its control, and the actions
 * at the end of the row — "Caută" when there is a text search to submit, then "Resetează".
 *
 * Built out of /stiri's filter bar so the next page with filters takes the same card rather than
 * growing a fifth private copy. The page says only what differs — which fields, what they hold, what
 * a change does — and the panel owns the rest: the layout, the label/control geometry (every control
 * 48px, MUD's .input height), the pair of buttons, and which of its lists is open, so opening one
 * closes the other and the page never wires isOpen/onToggle/onClose by hand.
 *
 * Fields: FilterSearch, FilterSelect and FilterDateRange, each in its own file. A field of another
 * kind joins the panel with a `filter-panel__field` wrapper, a `filter-panel__label` and, for a
 * pop-up control, useFilterPanelControl(name).
 *
 * `more` is opt-in: fields a page keeps behind a "Mai multe filtre" text button at the end of the
 * row, so the card does not take the first screen away from the list it filters. Folded unless
 * `moreOpen`. `variant="plain"` drops the card's frame, for a page whose list below is already
 * cards. Each action is opt-in too: "Caută" with `submitLabel`, "Resetează" with `resetLabel`.
 *
 * `layout="grid"` lays the fields on one grid of four tracks, the search two of them wide, and the
 * folded row on the same tracks — so its fields stand exactly under the first row's. The public
 * listings use it; the flexible row stays for the cabinet's panels.
 */
export function FilterPanel({
  ariaLabel,
  children,
  resetLabel,
  canReset,
  onReset,
  onSubmit,
  submitLabel,
  className,
  id,
  more,
  moreLabel,
  lessLabel,
  moreOpen = false,
  variant = 'card',
  layout = 'row',
}: FilterPanelProps) {
  const [open, setOpen] = useState<string | null>(null);
  const moreId = useId();
  const [showMore, setShowMore] = useState(moreOpen);
  // A filter in the folded part that becomes active later, as Back to a link with ?kind= does,
  // opens it: an active filter is never out of sight. Folding it again is the reader's to do.
  useEffect(() => {
    if (moreOpen) setShowMore(true);
  }, [moreOpen]);
  const close = useCallback(() => setOpen(null), []);
  const controls = useMemo(
    () => ({
      isOpen: (name: string) => open === name,
      toggle: (name: string) => setOpen((current) => nextOpenControl(current, name)),
      close,
    }),
    [open, close],
  );
  const submits = Boolean(onSubmit && submitLabel);
  const resets = Boolean(resetLabel && onReset);
  const classes = [
    variant === 'card' && 'card',
    'filter-panel',
    'filter-panel--row',
    layout === 'grid' && 'filter-panel--grid',
    (submits || resets) && 'filter-panel--with-actions',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      <div className="filter-panel__grid">
        {children}
        {more && (
          <button
            type="button"
            className="btn btn--text-primary filter-panel__more"
            aria-expanded={showMore}
            aria-controls={moreId}
            onClick={() => setShowMore((shown) => !shown)}
          >
            {showMore ? lessLabel : moreLabel}
          </button>
        )}
        {/* The actions together at the end of the row, level with the controls. A reset that is
            there is always there, off until something is filtered: appearing with the first filter
            and vanishing with the last would shift the card every time one changed. */}
        {(submits || resets) && (
          <div className="filter-panel__actions">
            {submits && (
              <button type="submit" className="btn btn--primary">
                {submitLabel}
              </button>
            )}
            {resets && (
              <button
                type="button"
                className="btn btn--text-primary"
                disabled={!canReset}
                onClick={onReset}
              >
                {resetLabel}
              </button>
            )}
          </div>
        )}
      </div>
      {more && (
        <div className="filter-panel__grid filter-panel__grid--more" id={moreId} hidden={!showMore}>
          {more}
        </div>
      )}
    </>
  );

  return (
    <FilterPanelContext.Provider value={controls}>
      {onSubmit ? (
        <form
          className={classes}
          id={id}
          role="search"
          aria-label={ariaLabel}
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {body}
          {/* Without a visible "Caută", Enter in the search still has to submit. Browsers do that
              for a form with one text field, but not once the period's two date fields are open
              too — then only a submit button makes Enter submit. Visually hidden rather than
              display:none, which some browsers skip; out of the tab order and hidden from
              assistive technology, since nobody is meant to reach it except through Enter. */}
          {!submits && (
            <button type="submit" className="visually-hidden" tabIndex={-1} aria-hidden="true" />
          )}
        </form>
      ) : (
        <div className={classes} id={id} role="search" aria-label={ariaLabel}>
          {body}
        </div>
      )}
    </FilterPanelContext.Provider>
  );
}
